import { canonicalizeGrokMediaModelId } from "./media-generation-contract.js";
/**
 * 去掉 `[...]` 标签后缀（如 `gpt-5.6-terra[1m]` → `gpt-5.6-terra`），得到 base id。
 *
 * **本仓这条归一规则的权威来源。** 此前它在三个 admin-portal-web 页面里各有一份逐字
 * 相同的定义——`[...]` 这个标签约定是事实副本，散落多处必漂移（不变量 #9）。新代码
 * 一律从这里 import；那三份存量副本按各自改动节奏收敛，不在引入本函数的 PR 里强改。
 */
export function stripModelIdTagSuffix(modelId) {
    return modelId.replace(/\[[^\]]*\]/g, "");
}
/**
 * 把一个 id 的三种写法都塞进查询集：原样、去标签后缀、Grok 媒体别名归一后的 canonical。
 *
 * 两侧（catalog 与策展）都要用它。少收一种，或只在一侧收，就会把「其实在」判成「不在」
 * ——2026-08-30 的 review 就抓到过只归一 catalog 那侧导致的假阳。
 */
function addIdVariants(set, modelId) {
    const id = modelId?.trim();
    if (!id)
        return;
    set.add(id);
    const base = stripModelIdTagSuffix(id);
    if (base)
        set.add(base);
    const canonical = canonicalizeGrokMediaModelId(id);
    if (canonical)
        set.add(canonical);
}
/** 把 catalog 摊成一个「这个 id 算不算在清单里」的查询集。 */
function catalogLookupSet(catalog) {
    const set = new Set();
    for (const entry of catalog)
        addIdVariants(set, entry.modelId);
    return set;
}
/** 策展里被提到过的全部 id（含 base 与 canonical 变体），供反方向判「有没有被圈过」。 */
function curatedIdSet(curation) {
    const set = new Set();
    const add = (modelId) => addIdVariants(set, modelId);
    for (const r of curation?.visionModels ?? [])
        add(r.modelId);
    for (const r of curation?.documentModels ?? [])
        add(r.modelId);
    for (const r of curation?.videoModels ?? [])
        add(r.modelId);
    for (const r of curation?.imageGenModels ?? [])
        add(r.modelId);
    for (const r of curation?.videoGenModels ?? [])
        add(r.modelId);
    for (const id of curation?.modelCallModels ?? [])
        add(id);
    for (const id of curation?.imageFallbackChain ?? [])
        add(id);
    return set;
}
/**
 * 一个 catalog 行落在哪些工具板块。
 *
 * `activeSections` = **已经被策展配置过**的板块。没配过的板块整个不参与反方向对账——
 * 那个语义是「未配置 = 该能力不可用」，不是「超管漏配了全世界」。
 *
 * ⚠️ 这道门是拿真实生产数据跑出来才发现要加的（2026-08-30）：策展 `visionModels` 为空、
 * 而网关清单里几乎每个聊天模型都带 vision 标签，不设门就一口气吐 22 条「看图/claude-opus-5」
 * 这类噪音，把唯一真正要看的那条（生图/grok-imagine-image-2.0）淹掉。**噪音不是小瑕疵，
 * 它会让人整体无视这个信号**，等于把机制关掉——正是本模块存在的意义的反面。
 */
function toolCapableSections(entry, activeSections) {
    const tags = entry.capabilityTags ?? [];
    const sections = [];
    if (activeSections.has("生图") && (tags.includes("image_gen") || tags.includes("image_generation"))) {
        sections.push("生图");
    }
    if (activeSections.has("看图") && (entry.capabilities?.supportsVision || tags.includes("vision"))) {
        sections.push("看图");
    }
    return sections;
}
/** 哪些板块已被策展配置过（非空数组才算配过）。 */
function activeCuratedSections(curation) {
    const active = new Set();
    if (curation?.imageGenModels?.length)
        active.add("生图");
    if (curation?.visionModels?.length)
        active.add("看图");
    if (curation?.documentModels?.length)
        active.add("读 PDF");
    if (curation?.videoModels?.length)
        active.add("视频理解");
    return active;
}
/**
 * 双向对账。
 *
 * @param curation 已保存的平台工具策展（undefined = 未配置，此时正方向必然为空——
 *   未配置 = 该能力不可用，不构成「配了但没有」）。
 * @param catalog **live** 网关清单条目。调用方必须已确认它不是随包快照回落（见头注 §坑 2）。
 */
export function reconcileCurationAgainstCatalog(curation, catalog) {
    const inCatalog = catalogLookupSet(catalog);
    const staleCurated = [];
    const seenStale = new Set();
    const checkStale = (section, modelId, group) => {
        const id = modelId?.trim();
        if (!id)
            return;
        // 三种写法都要认：原样、去标签后缀、Grok 媒体别名归一。
        //
        // **归一必须两侧都做**。只归一 catalog 那侧会在「策展写别名、清单写 canonical」时
        // 假阳——而那条在运行时是**可用**的（`deriveImageGenModels` 对策展 modelId 也做
        // canonicalize）。策展行允许手填（超管页输入框明说「也可手填新 id」），所以这条路径
        // 真实存在。假阳出现在一个「专门用来避免假红」的机制里是最坏的一种：误报一次，
        // 超管下次就不看横幅了。
        const canonical = canonicalizeGrokMediaModelId(id);
        if (inCatalog.has(id) || inCatalog.has(stripModelIdTagSuffix(id)) || (canonical && inCatalog.has(canonical)))
            return;
        const key = `${section}|${id}|${group ?? ""}`;
        if (seenStale.has(key))
            return;
        seenStale.add(key);
        staleCurated.push({ section, modelId: id, ...(group ? { group } : {}) });
    };
    for (const r of curation?.visionModels ?? [])
        checkStale("看图", r.modelId);
    for (const r of curation?.documentModels ?? [])
        checkStale("读 PDF", r.modelId);
    for (const r of curation?.videoModels ?? [])
        checkStale("视频理解", r.modelId);
    for (const r of curation?.imageGenModels ?? [])
        checkStale("生图", r.modelId, r.group);
    for (const r of curation?.videoGenModels ?? [])
        checkStale("视频生成", r.modelId, r.group);
    for (const id of curation?.modelCallModels ?? [])
        checkStale("干活模型池", id);
    for (const id of curation?.imageFallbackChain ?? [])
        checkStale("图片理解降级链", id);
    const curated = curatedIdSet(curation);
    const activeSections = activeCuratedSections(curation);
    const uncuratedCatalog = [];
    const seenUncurated = new Set();
    for (const entry of catalog) {
        if (!entry.modelId)
            continue;
        if (activeSections.size === 0)
            break; // 一个板块都没配 = 全走内置基线，反方向无从谈起
        if (curated.has(entry.modelId) || curated.has(stripModelIdTagSuffix(entry.modelId)))
            continue;
        const canonical = canonicalizeGrokMediaModelId(entry.modelId);
        if (canonical && curated.has(canonical))
            continue;
        for (const section of toolCapableSections(entry, activeSections)) {
            const key = `${section}|${entry.modelId}`;
            if (seenUncurated.has(key))
                continue;
            seenUncurated.add(key);
            uncuratedCatalog.push({ section, modelId: entry.modelId });
        }
    }
    return {
        staleCurated,
        uncuratedCatalog,
        inSync: staleCurated.length === 0 && uncuratedCatalog.length === 0,
    };
}
/** 一行人话摘要，给日志/审计用（两个方向各报计数，零漂移时返回 undefined）。 */
export function summarizeCurationDrift(r) {
    if (r.inSync)
        return undefined;
    const parts = [];
    if (r.staleCurated.length > 0) {
        parts.push(`${r.staleCurated.length} 个已配置模型不在网关清单里（员工端已看不到）：` +
            r.staleCurated.map((d) => `${d.section}/${d.modelId}`).join("、"));
    }
    if (r.uncuratedCatalog.length > 0) {
        parts.push(`${r.uncuratedCatalog.length} 个清单里的模型未被策展（员工端从未出现）：` +
            r.uncuratedCatalog.map((d) => `${d.section}/${d.modelId}`).join("、"));
    }
    return parts.join("；");
}
/**
 * 最终 param_schema = **网关 ∩ 策展覆盖**（2026-08-30 用户拍板：覆盖只能收窄，不能放宽）。
 *
 * ## 为什么不是「覆盖直接替换」
 *
 * 原实现是 `cur.paramSchema ?? row.capabilities?.paramSchema`——策展只要填了，网关那份就
 * **完全不看**。于是策展可以放行网关根本不支持的取值，请求一路发出去、在网关侧才失败，
 * 而失败点离配置很远，没人能把它连回「超管当初多填了一个值」。
 *
 * ## 为什么不干脆去掉覆盖位
 *
 * 覆盖位有真实用途：网关 catalog 行**按 modelId 键，没有 per-组表达力**——一条 `gpt-image-2`
 * 行说不清「`input_fidelity` 在 packy 组放行、在 OAuth 组拒」。所以「同一模型在不同凭证组下
 * 参数不同」只能靠覆盖表达。取交集同时保住这个能力和「不越界」这条线。
 *
 * ## 边界（每条都对应一种会出错的输入）
 *
 * - **网关侧没有该参数的枚举**（`gatewaySchema` 缺该 key）→ 网关没给上界，**沿用覆盖**。
 *   若改成「削光」，任何网关暂未描述的参数都会被误杀成空集 = 该参数彻底不可用。
 * - **网关侧整份 schema 缺失**（undefined）→ 无上界可言，沿用覆盖（同上，防误杀）。
 * - **交集为空**（覆盖里一个合法值都没有）→ 返回空数组并记违规。空集是诚实结果：
 *   它表示「这个参数在这个组下无可用取值」，比悄悄放行越界值好。
 */
export function intersectParamSchema(modelId, gatewaySchema, curationOverride) {
    if (!curationOverride)
        return { schema: gatewaySchema, violations: [] };
    if (!gatewaySchema)
        return { schema: curationOverride, violations: [] };
    const out = {};
    const violations = [];
    for (const [param, overrideValues] of Object.entries(curationOverride)) {
        const allowed = gatewaySchema[param];
        if (!allowed) {
            // 网关没描述这个参数 → 没有上界可比，沿用覆盖（别误杀）。
            out[param] = [...overrideValues];
            continue;
        }
        const allowedSet = new Set(allowed);
        const kept = overrideValues.filter((v) => allowedSet.has(v));
        const rejected = overrideValues.filter((v) => !allowedSet.has(v));
        out[param] = kept;
        if (rejected.length > 0) {
            violations.push({
                modelId,
                param,
                rejected,
                emptyIntersection: kept.length === 0 && overrideValues.length > 0,
            });
        }
    }
    // 网关有、覆盖没提的参数：覆盖是「整字段替换」语义，没提 = 不暴露该参数，保持不变。
    return { schema: out, violations };
}
/** 一行人话，给 console.warn / console.error / 审计用。空交集必须整句不同——三种病表现相同但排查方向不同。 */
export function describeParamNarrowing(v) {
    if (v.emptyIntersection) {
        return `${v.modelId} 的 ${v.param}：该参数在该组下一个可用取值都没有了。这几乎一定是两侧 param_schema 编码不同形（区间串 vs 枚举），不是超管真的想禁用它。排查方向：比对网关与策展两侧该参数的取值形状，不要当越界收窄处理`;
    }
    return `${v.modelId} 的 ${v.param}：策展想放行 ${v.rejected.join("/")}，但网关不允许，已削掉`;
}
// ── 下发时剔除：网关没有的行不下发 ──────────────────────────────────
/**
 * 把策展里「不在 live 清单中」的行剔掉，返回可安全下发的那一份。
 *
 * 2026-08-30 用户拍板：「网关提供候选列表，没有的就应该消失」——消失要在**每一层**都成立，
 * 不能只靠客户端 `derive*` 的交集静默跳过。在下发这一刻剔除，员工端拿到的 bundle 就已经
 * 是干净的，而剔除动作本身可被审计。
 *
 * ## 幂等
 *
 * 同一份 (curation, catalog) 恒产出同一结果，所以 etag 只在**网关清单真的变了**的时候才
 * churn。这一点是本函数能挂在 bootstrap 热路径上的前提：不幂等就会让全体 desktop 反复
 * 全量重同步。
 *
 * ## 调用方仍须自证 catalog 是 live 的
 *
 * 拿随包快照来剔，会把**在用的**模型剔掉——比不剔严重得多。同 {@link reconcileCurationAgainstCatalog}，
 * 本函数无法自证新鲜度，门在调用方（有用例钉住 snapshot 档零剔除）。
 *
 * @returns `curation` = 剔除后的策展（无剔除时**返回原对象引用**，便于调用方零成本判断）；
 *   `removed` = 被剔掉的行，供审计/告警。
 */
export function pruneCurationToCatalog(curation, catalog) {
    if (!curation)
        return { curation, removed: [] };
    const { staleCurated } = reconcileCurationAgainstCatalog(curation, catalog);
    if (staleCurated.length === 0)
        return { curation, removed: [] };
    const dead = new Set(staleCurated.map((d) => `${d.section}|${d.modelId}`));
    const alive = (section, modelId) => !dead.has(`${section}|${(modelId ?? "").trim()}`);
    const next = { ...curation };
    if (curation.visionModels)
        next.visionModels = curation.visionModels.filter((r) => alive("看图", r.modelId));
    if (curation.documentModels)
        next.documentModels = curation.documentModels.filter((r) => alive("读 PDF", r.modelId));
    if (curation.videoModels)
        next.videoModels = curation.videoModels.filter((r) => alive("视频理解", r.modelId));
    if (curation.imageGenModels)
        next.imageGenModels = curation.imageGenModels.filter((r) => alive("生图", r.modelId));
    if (curation.videoGenModels)
        next.videoGenModels = curation.videoGenModels.filter((r) => alive("视频生成", r.modelId));
    if (curation.modelCallModels)
        next.modelCallModels = curation.modelCallModels.filter((id) => alive("干活模型池", id));
    if (curation.imageFallbackChain) {
        next.imageFallbackChain = curation.imageFallbackChain.filter((id) => alive("图片理解降级链", id));
    }
    return { curation: next, removed: staleCurated };
}
//# sourceMappingURL=curation-catalog-reconcile.js.map